import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import "./index.css";

const TIERS = [
  {
    id: "tier-1",
    label: "Tier 01",
    vram: "64GB VRAM",
    config: "Dual RTX 5090",
    useCase: "Inference & Light Fine-Tuning",
    summary:
      "Dual-card local LLM inference, 13B to 30B fine-tuning, and Stable Diffusion training with full RAG pipelines.",
    models: [
      "Llama 3 70B (4-bit / 8-bit)",
      "Qwen 2.5 72B (4-bit)",
      "Mistral 7B / Mixtral 8x7B",
      "DeepSeek V3 (heavy quantization)",
      "Stable Diffusion XL + LoRA training"
    ],
    shopLabel: "32GB",
    shopHref: "https://arsenalpc.com/shop/ai-workstations/?filter_vram%5B%5D=32gb"
  },
  {
    id: "tier-2",
    label: "Tier 02",
    vram: "96GB VRAM",
    config: "RTX Pro 6000 Blackwell",
    useCase: "Single-Card Production Inference",
    summary:
      "Run 70B parameter models at full precision on one card, with simple deployment and headroom for long-context inference and active fine-tuning.",
    featured: true,
    models: [
      "Llama 3 70B (FP16 full precision)",
      "Qwen 2.5 72B (FP16)",
      "Mixtral 8x22B",
      "DeepSeek V3 (4-bit)",
      "70B LoRA fine-tuning + RAG pipelines"
    ],
    shopLabel: "96GB",
    shopHref: "https://arsenalpc.com/shop/ai-workstations/?filter_vram%5B%5D=96gb"
  },
  {
    id: "tier-3",
    label: "Tier 03",
    vram: "192GB VRAM",
    config: "Dual RTX Pro 6000 Blackwell",
    useCase: "Large Model Training & Multi-Modal",
    summary:
      "Train, fine-tune, and serve the largest open source LLMs locally with enough memory headroom for long-context and multi-modal workloads.",
    models: [
      "Llama 3.1 405B (4-bit)",
      "DeepSeek V3 (8-bit)",
      "70B fine-tuning at full precision",
      "Multi-modal training (vision + LLM)",
      "Long-context inference workloads"
    ],
    shopLabel: "192GB",
    shopHref: "https://arsenalpc.com/shop/ai-workstations/?filter_vram%5B%5D=192gb"
  }
];

export default function App() {
  const rootRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const inView = useInView(rootRef, { margin: "-10% 0px", once: true });
  const shouldAnimate = inView && !prefersReducedMotion;

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 18 },
    animate: shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }
  });

  return (
    <section ref={rootRef} className="ll">
      <div className="ll-inner">
        <div className="ll-header">
          <motion.span className="ll-eyebrow" {...fade(0)}>
            <span className="ll-eyebrow-mark" />
            Local LLM Capability
          </motion.span>
          <motion.h2 className="ll-title" {...fade(0.1)}>
            Run the{" "}
            <span className="ll-title-accent">largest open source LLMs</span>{" "}
            on your own hardware
          </motion.h2>
          <motion.p className="ll-subtitle" {...fade(0.2)}>
            Every ArsenalPC AI workstation is configurable across three local
            LLM tiers, from 64GB dual-card inference to 192GB of VRAM for
            full-precision Llama 3 405B and dense fine-tuning workloads.
          </motion.p>
        </div>

        <div className="ll-grid">
          {TIERS.map((tier, i) => (
            <motion.article
              key={tier.id}
              className={`ll-card${tier.featured ? " ll-card-featured" : ""}`}
              initial={{ opacity: 0, y: 24 }}
              animate={
                shouldAnimate
                  ? { opacity: 1, y: 0 }
                  : { opacity: 1, y: 0 }
              }
              transition={{
                duration: 0.7,
                delay: 0.3 + i * 0.1,
                ease: [0.22, 1, 0.36, 1]
              }}
            >
              <header className="ll-card-head">
                <span className="ll-card-label">{tier.label}</span>
                {tier.featured && (
                  <span className="ll-card-flag">Most Popular</span>
                )}
              </header>

              <div className="ll-card-vram">{tier.vram}</div>
              <div className="ll-card-config">{tier.config}</div>

              <h3 className="ll-card-usecase">{tier.useCase}</h3>
              <p className="ll-card-summary">{tier.summary}</p>

              <ul className="ll-card-models">
                {tier.models.map((m) => (
                  <li key={m} className="ll-card-model">
                    <span className="ll-card-model-bullet" aria-hidden="true" />
                    {m}
                  </li>
                ))}
              </ul>

              <a className="ll-card-shop" href={tier.shopHref}>
                Shop {tier.shopLabel} VRAM Workstations
                <span className="ll-card-shop-arrow" aria-hidden="true">→</span>
              </a>
            </motion.article>
          ))}
        </div>

        <motion.div className="ll-footer" {...fade(0.6)}>
          <p className="ll-footer-note">
            Need help sizing VRAM for a specific model or workload? We help
            researchers and ML teams pick the right configuration.
          </p>
          <div className="ll-footer-cta-wrap">
            <a
              href="tel:+14406027090"
              className="ll-footer-cta"
              aria-describedby="ll-footer-cta-hours"
            >
              <svg
                className="ll-footer-cta-icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              Talk to a Specialist
            </a>
            <span
              id="ll-footer-cta-hours"
              className="ll-footer-cta-tooltip"
              role="tooltip"
            >
              Mon–Fri 10am–6pm EST · Sat 11am–3pm EST
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
